// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useEffect, useState } from 'react';

import { extractErrorMessage } from '@evolvemep/api-ts';
import {
  Button,
  Checkbox,
  Flex,
  Input,
  LoadingOverlay,
  PasswordInput,
  Text,
  TextInput,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
// oxlint-disable-next-line no-restricted-imports
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';

import { EULAModal } from 'app/EULAModal';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { isNotNil } from 'helpers/isNotNil';
import { useMaybeUser } from 'hooks/user/useMaybeUser';

import { useConfirmUser, useGetAuthenticationUrl } from '../auth/restCalls';
import { useSignIn } from '../auth/useSignIn';
import { AuthFormWrapper, loginAnimationDuration } from '../components/AuthFormWrapper';
import type { LoginErrorCode } from '../components/LoginErrorAlert';
import { buildSsoStateVar } from './helper';

export const Login = () => {
  const theme = useMantineTheme();
  const { confirmUser } = useConfirmUser();
  const { user: existingUser, refreshUser, loading: loadingUser } = useMaybeUser();
  const { signIn } = useSignIn();
  const [signingIn, setSigningIn] = useState(false);
  const { getAuthenticationUrl, loading: loadingAuthenticationUrl } = useGetAuthenticationUrl();

  const [isConfirmed, setIsConfirmed] = useState(false);
  // useEvolveNavigate only works inside Shop routes
  const navigate = useNavigate();
  const {
    // From src/modules/Authentication/ResetPassword.tsx
    resetPassword: changePasswordSuccess,
    // From src/modules/Authentication/Join/Join.tsx
    inviteConfirm: confirmationSuccess,
    // src/modules/Authentication/AcceptInvitation.tsx
    joinConfirm: joinSuccess,
  } = (useLocation().state ?? {}) as {
    resetPassword?: boolean;
    inviteConfirm?: boolean;
    joinConfirm?: string;
  };

  const [query] = useSearchParams();
  const queryErrorCode = query.get('errorCode') as LoginErrorCode | null;
  const username = query.get('username');
  const email = query.get('email');
  const code = query.get('code');
  const redirect = query.get('redirect') ?? '/';
  const [errorCode, setErrorCode] = useState<LoginErrorCode | string | undefined>(queryErrorCode ?? undefined);
  const [loginState, setLoginStage] = useState<
    'ask-email' | 'transitioning-to-password' | 'ask-password' | 'transitioning-to-email'
  >('ask-email');

  useEffect(() => {
    if (errorCode) setSigningIn(false);
  }, [errorCode]);

  const form = useForm<{
    email: string;
    password: string;
    keepEnabled: boolean;
  }>({
    initialValues: {
      email: email ?? '',
      password: '',
      keepEnabled: false,
    },
    validate: {
      email: (v) => !v,
      password: (p) => loginState !== 'ask-email' && !p,
    },
  });

  const [successMessage, setSuccessMessage] = useState<string>();

  useEffect(() => {
    if (joinSuccess) {
      setSuccessMessage(`You have joined ${joinSuccess}! Login to continue.`);
    }
  }, [joinSuccess]);
  useEffect(() => {
    if (isConfirmed) {
      setSuccessMessage('Your email address has been verified!');
    }
  }, [isConfirmed]);
  useEffect(() => {
    if (changePasswordSuccess) {
      setSuccessMessage('Success! Your new password has been set.');
    }
  }, [changePasswordSuccess]);
  useEffect(() => {
    if (confirmationSuccess) {
      setSuccessMessage('Success! You have successfully joined.');
    }
  }, [confirmationSuccess]);

  useEffect(() => {
    if (!isConfirmed && username && code) {
      confirmUser(username, 'company', { confirmationCode: code })
        .then(() => setIsConfirmed(true))
        .catch(() => setIsConfirmed(false));
    }
  }, [code, confirmUser, isConfirmed, username]);

  const handleSignIn = async (values: typeof form.values) => {
    setErrorCode(undefined);
    setSuccessMessage(undefined);
    setSigningIn(true);

    try {
      await signIn(values);
      const user = await refreshUser();
      if (!user) {
        setErrorCode('UnknownException');
      } else if (user.acceptedLatestEula) {
        void navigate(redirect);
      }
    } catch (e) {
      setErrorCode(extractErrorMessage(e));
    }
  };

  const checkUserAuthenticationUrl = async (values: typeof form.values) => {
    setErrorCode(undefined);
    // make a request to get the login redirect URL
    await getAuthenticationUrl(`email/${values.email}`)
      .then((urlResponse) => {
        const destinationURL = urlResponse?.returnUrl;
        if (destinationURL?.startsWith('https://')) {
          window.location.href = `${destinationURL}&state=${buildSsoStateVar({ previousLoc: redirect })}`;
        } else {
          form.setFieldValue('password', '');
          setLoginStage('transitioning-to-password');
          setTimeout(() => {
            setLoginStage('ask-password');
          }, loginAnimationDuration);
        }
      })
      .catch((err: unknown) => {
        if (err === 'Not Found') {
          setErrorCode('UserNotFoundException');
        } else {
          setErrorCode('UnknownException');
        }
      })
      .finally(() => {
        setSigningIn(false);
      });
  };

  if (loginState === 'ask-password' || loginState === 'transitioning-to-email') {
    return (
      <AuthFormWrapper
        title="Login"
        form={form}
        onSubmit={handleSignIn}
        errorCode={errorCode}
        onCloseError={() => setErrorCode(undefined)}
        successMessage={successMessage}
        onCloseSuccess={() => setSuccessMessage(undefined)}
        boxProps={{
          style: {
            opacity: loginState === 'ask-password' ? 1 : 0,
            marginLeft: loginState === 'ask-password' ? 0 : -50,
            transition: `all ${loginAnimationDuration}ms ease`,
          },
        }}
      >
        <>
          <Input.Label mb={-10} size="md">
            Email
          </Input.Label>
          <Text c="gray.9">{form.values.email}</Text>
          <PasswordInput
            label="Password"
            data-cy="password-input"
            name="password"
            size="md"
            disabled={signingIn}
            autoFocus
            {...form.getInputProps('password')}
          />
          <Flex my="lg" justify="space-between" align="center">
            <Checkbox
              color="dark"
              size="md"
              label={
                <Flex align="center" gap={6}>
                  <Text>Keep me signed in</Text>
                  <InfoIcon label="Leave this unchecked if this is a shared device." />
                </Flex>
              }
              disabled={signingIn}
              {...form.getInputProps('keepEnabled')}
            />
            <Link
              className="link"
              data-cy="forgot-password"
              to="/auth/forgot-password"
              state={{ email: form.values.email }}
            >
              <Text c={signingIn ? 'dimmed' : theme.primaryColor}>Forgot Password?</Text>
            </Link>
          </Flex>
          <Button
            type="submit"
            loading={signingIn}
            disabled={!form.isValid()}
            fullWidth
            color={`${theme.primaryColor}.9`}
          >
            Login
          </Button>
          <Flex justify="center" align="center" gap={4}>
            <Text fz="sm" c="dimmed">
              Wrong email?
            </Text>
            <Text
              fz="sm"
              c={signingIn ? 'dimmed' : undefined}
              className="link"
              onClick={() => {
                setLoginStage('transitioning-to-email');
                setTimeout(() => setLoginStage('ask-email'), loginAnimationDuration);
              }}
            >
              Change login
            </Text>
          </Flex>
        </>
        <EULAModal onEulaAccepted={() => navigate(redirect)} />
      </AuthFormWrapper>
    );
  }

  if (isNotNil(existingUser)) {
    return (
      <AuthFormWrapper title="" form={form} onSubmit={handleSignIn}>
        <Input.Label mb={-10} size="md">
          Email
        </Input.Label>
        <Text c="gray.9">{existingUser.userEmail}</Text>
        <EULAModal onEulaAccepted={() => navigate(redirect)} />
      </AuthFormWrapper>
    );
  }

  return (
    <AuthFormWrapper
      title="Login"
      form={form}
      onSubmit={checkUserAuthenticationUrl}
      errorCode={errorCode}
      onCloseError={() => setErrorCode(undefined)}
      successMessage={successMessage}
      onCloseSuccess={() => setSuccessMessage(undefined)}
      boxProps={{
        style: {
          opacity: loginState === 'ask-email' ? 1 : 0,
          marginLeft: loginState === 'ask-email' ? 0 : -50,
          transition: `all ${loginAnimationDuration}ms ease`,
        },
      }}
    >
      <>
        <TextInput
          label="Email"
          name="email"
          data-cy="email-input"
          size="md"
          autoFocus
          type="email"
          disabled={loadingAuthenticationUrl || loginState === 'transitioning-to-password'}
          {...form.getInputProps('email')}
        />
        <Button
          type="submit"
          loading={loadingAuthenticationUrl || loginState === 'transitioning-to-password'}
          disabled={!form.isValid()}
          fullWidth
          color={`${theme.primaryColor}.9`}
        >
          Next
        </Button>
      </>
      <LoadingOverlay visible={loadingUser} />
    </AuthFormWrapper>
  );
};
